/**
 * PKCE (RFC 7636 §4.1) verifier + S256 challenge helpers.
 * Dependency-free — everything is Web Crypto, available in every supported browser.
 * The challenge method is always S256; the `plain` fallback is never used.
 */

/** Convert a byte value (0-255) to a zero-padded two-char hex string. */
const dec2hex = (dec: number): string => `0${dec.toString(16)}`.slice(-2);

/** RFC 7636 code verifier: a 56-char high-entropy hex string from `crypto.getRandomValues`. */
const generateCodeVerifier = (): string => {
	const array = new Uint32Array(56 / 2);
	window.crypto.getRandomValues(array);
	return Array.from(array, dec2hex).join("");
};

/** SHA-256 digest of the input string. */
const sha256 = (plain: string): Promise<ArrayBuffer> => {
	const data = new TextEncoder().encode(plain);
	return window.crypto.subtle.digest("SHA-256", data);
};

/** base64url-encode an ArrayBuffer (+→-, /→_, strip trailing =). */
const base64UrlEncode = (buffer: ArrayBuffer): string => {
	const bytes = new Uint8Array(buffer);
	let str = "";
	for (const byte of bytes) {
		str += String.fromCharCode(byte);
	}
	return window.btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

/** S256 challenge derived from the verifier. */
const generateCodeChallengeFromVerifier = async (verifier: string): Promise<string> =>
	base64UrlEncode(await sha256(verifier));

/** Generate a matched PKCE { verifier, challenge } pair (S256). */
export const generateVerifierAndChallenge = async (): Promise<{ verifier: string; challenge: string }> => {
	const verifier = generateCodeVerifier();
	const challenge = await generateCodeChallengeFromVerifier(verifier);
	return { challenge, verifier };
};
